'use client';
import {
  Ach,
  AdyenCheckout,
  Card,
  type CardBinLookupData,
  GooglePay,
  type OnChangeData,
  type SubmitActions,
  type SubmitData,
  type UIElement,
} from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {Checkbox} from '../Checkbox';
import type {Document} from '@contentful/rich-text-types';
import clsx from 'clsx';
import {useCallback, useMemo, useRef, useState} from 'react';
import React from 'react';
import {RichDiv} from '../../rich-text';
import {Accordion, AccordionDetails} from '../Accordion';
import {Button} from '../Button';
import {GPayLogo} from '../GPayLogo';
import {
  AccountBalanceIcon,
  CreditCardOutlinedIcon,
  ErrorIcon,
  LockOutlinedIcon,
} from '../Icon';
import {IconButton} from '../IconButton';
import {Radio} from '../Radio';
import {TextField} from '../TextField';
import styles from './AddPaymentMethod.module.scss';
import type {
  PaymentWidget,
  StorePaymentMethodHandler,
  paymentMethodType,
} from './AddPaymentMethod.types';

export type AddPaymentMethodProps = {
  paymentWidget: PaymentWidget;
  locale?: 'en-US' | 'es-US';
  onStorePaymentMethod: StorePaymentMethodHandler;
  onPaymentCompleted?: () => void;
  onClose?: () => void;
};
const AddPaymentMethod = ({
  paymentWidget,
  locale,
  onStorePaymentMethod,
  onPaymentCompleted,
  onClose,
}: AddPaymentMethodProps) => {
  const onAdyenSubmit = useCallback(
    async (state: SubmitData, component: UIElement, actions: SubmitActions) => {
      try {
        const result = await onStorePaymentMethod({
          encryptedPaymentMethodDetails: state.data.paymentMethod,
        });

        if (result.paymentErrorWidget || !result.storePaymentMethodSuccess) {
          setCheckoutErrorRichText(
            result.paymentErrorWidget?.messageRichText ?? null,
          );
          actions.resolve({resultCode: 'Received'});
          return;
        }

        actions.resolve({
          resultCode: result.storePaymentMethodSuccess.resultCode,
        });
      } catch (error) {
        console.error('onSubmit', error);
        actions.reject();
      }
    },
    [onStorePaymentMethod],
  );

  const onCheckoutStateChange = useCallback(
    (state: OnChangeData, el: UIElement) => {
      if (el.type === 'scheme') {
        setCardStateValid(state.isValid);
      }
      if (el.type === 'ach') {
        setAchStateValid(state.isValid);
      }
    },
    [],
  );

  const checkout = useMemo(
    () =>
      AdyenCheckout({
        clientKey: paymentWidget.clientKey,
        environment: 'test',
        countryCode: 'US',
        locale,
        onSubmit: onAdyenSubmit,
        onPaymentCompleted,
        onError: e => {
          console.log(e);
        },
        onChange: onCheckoutStateChange,
      }),
    [
      locale,
      paymentWidget.clientKey,
      onAdyenSubmit,
      onPaymentCompleted,
      onCheckoutStateChange,
    ],
  );

  const cardComponent = useRef<Card | null>(null);
  const achComponent = useRef<Ach | null>(null);
  const googleComponent = useRef<GooglePay | null>(null);

  const initializeComponent = useCallback(
    async (node: HTMLDivElement | null, methodType?: paymentMethodType) => {
      if (!node) return;

      switch (methodType) {
        case 'SCHEME':
          if (!cardComponent.current) {
            cardComponent.current = new Card(await checkout, {
              hasHolderName: true,
              holderNameRequired: true,
              showBrandIcon: true,
              brands: ['mc', 'amex', 'visa', 'discover'],
              positionHolderNameOnTop: true,
              showPayButton: false,
              onBinLookup,
            }).mount(node);
          }
          return;
        case 'ACH':
          if (!cardComponent.current) {
            achComponent.current = new Ach(await checkout, {
              hasHolderName: true,
              holderNameRequired: true,
              enableStoreDetails: false,
              showPayButton: false,
            }).mount(node);
          }
          return;
        case 'PAYWITHGOOGLE':
          if (!googleComponent.current) {
            googleComponent.current = new GooglePay(await checkout);
          }
      }
    },
    [checkout],
  );

  const [paymentMethod, setPaymentMethod] = useState<
    'ACH' | 'SCHEME' | 'PAYWITHGOOGLE' | null
  >(null);
  const [cardCountryCode, setCardCountryCode] = useState<string | undefined>(
    undefined,
  );
  const [cardStateValid, setCardStateValid] = useState<boolean>(false);
  const [achStateValid, setAchStateValid] = useState<boolean>(false);
  const [checkoutErrorRichText, setCheckoutErrorRichText] =
    useState<Document | null>(null);
  const [tosAccepted, setTosAccepted] = useState<boolean>(false);

  const onBinLookup = ({issuingCountryCode}: CardBinLookupData) => {
    setCardCountryCode(issuingCountryCode);
  };

  const handleSubmitButton = () => {
    if (paymentMethod === 'SCHEME') {
      cardComponent.current?.submit();
    } else if (paymentMethod === 'ACH') {
      achComponent.current?.submit();
    } else if (paymentMethod === 'PAYWITHGOOGLE') {
      googleComponent.current?.submit();
    }
  };

  const acceptedCountryCode = !cardCountryCode || cardCountryCode === 'US';
  const hasFieldErrors =
    (paymentMethod === 'SCHEME' && !cardStateValid) ||
    (paymentMethod === 'ACH' && !achStateValid);
  const submitButtonEnabled =
    tosAccepted && !!paymentMethod && acceptedCountryCode && !hasFieldErrors;

  return (
    <div className={styles.addPaymentMethodContainer}>
      <div className={styles.headerContainer}>
        <RichDiv className={styles.headerText}>
          {paymentWidget.headerRichText}
        </RichDiv>
        <IconButton
          onClick={onClose}
          icon="close"
          aria-label="close"
          data-testid={'modal-close-button'}
          className={styles.closeIcon}
        />
      </div>
      <div className={styles.middleSection}>
        <RichDiv className={styles.subHeaderText}>
          {paymentWidget.subHeaderRichText}
        </RichDiv>

        <div className={styles.subHeader2}>
          <LockOutlinedIcon className={styles.secureIcon} />
          <RichDiv className={styles.secureText}>
            {paymentWidget.secureRichText}
          </RichDiv>
        </div>

        {checkoutErrorRichText && (
          <div className={styles.checkoutErrorsWrapper}>
            <ErrorIcon />
            <RichDiv>{checkoutErrorRichText}</RichDiv>
          </div>
        )}

        <div className={styles.accordionGroup}>
          {paymentWidget.paymentMethods?.map(method => (
            <Accordion
              key={method.methodType}
              open={paymentMethod === method.methodType}
              className={clsx(
                styles.accordion,
                paymentMethod === method.methodType && styles.open,
              )}
              handle={
                <div className={styles.accordionSummary}>
                  <Radio
                    className={styles.paymentTypeRadio}
                    id={`${method.methodType}-radio`}
                    checked={paymentMethod === method.methodType}
                    onChange={() => setPaymentMethod(method.methodType ?? null)}
                  />
                  <label
                    htmlFor={`${method.methodType}-radio`}
                    className={styles.paymentMethodLabel}
                  >
                    {method.methodType === 'SCHEME' && (
                      <div className={styles.paymentMethodIconWrapper}>
                        <CreditCardOutlinedIcon />
                      </div>
                    )}
                    {method.methodType === 'ACH' && (
                      <div className={styles.paymentMethodIconWrapper}>
                        <AccountBalanceIcon />
                      </div>
                    )}
                    {method.methodType === 'PAYWITHGOOGLE' && (
                      <GPayLogo className={styles.GPayLogo} />
                    )}
                    <span className={styles.paymentMethodLabelText}>
                      {method.displayText?.header}
                    </span>
                  </label>
                </div>
              }
            >
              <AccordionDetails className={styles.accordionDetails}>
                <div className={styles.cardContainer}>
                  <div
                    ref={node => {
                      initializeComponent(node, method.methodType);
                    }}
                  />
                  {(paymentMethod === 'ACH' || paymentMethod === 'SCHEME') && (
                    <TextField
                      className={styles.nicknameTextField}
                      outlined
                      label="Payment Nickname (optional)"
                    />
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>

        <div className={styles.tosWrapper}>
          <Checkbox
            className={styles.checkbox}
            checked={tosAccepted}
            onChange={() => setTosAccepted(!tosAccepted)}
          />
          <RichDiv className={styles.tosText}>
            {paymentWidget.termsOfServiceRichText}
          </RichDiv>
        </div>
      </div>

      <Button
        className={styles.submitButton}
        disabled={!submitButtonEnabled}
        onClick={handleSubmitButton}
        widget={{title: 'Accept and continue'}}
        round
      />
    </div>
  );
};

export {AddPaymentMethod};
